import type { CsvImportStatus } from '@/lib/csv-import/api';
import { ApiError } from '@/lib/errors';
import { apiGet, apiPost } from '@/lib/http/client';
import {
  type MappingAction,
  type MappingProposal,
  type VendorJob,
  VendorJobFailedError as LightfieldJobFailedError,
  type VendorJobStatus,
  VendorJobTimeoutError as LightfieldJobTimeoutError,
  waitForVendorJob,
} from '@/lib/vendor-import';

export interface ValidateKeyResponse {
  valid: boolean;
}

export interface DiscoveredObjectType {
  label: string;
  entity_type_name: string;
}

export interface DiscoverTypesResponse {
  custom_object_types: DiscoveredObjectType[];
}

/**
 * Both heavy wizard steps are dispatched, not run inline: they walk every record
 * at Lightfield's 25-rows-per-page cap, which used to run inside one request
 * against a 30s gunicorn timeout and completed only for demo-sized orgs. The
 * POST returns 202 with a job id; the client polls until terminal.
 */
export interface StartJobResponse {
  job_id: number;
}

export type LightfieldJobStatus = VendorJobStatus;

export type LightfieldJobErrorCode =
  | 'lightfield_unreachable'
  | 'invalid_key'
  | 'no_importable_types'
  | 'invalid_mappings'
  | 'internal';

export interface LightfieldJob extends VendorJob<LightfieldJobErrorCode> {
  kind: 'proposals' | 'create';
}

export interface CreateImportResult {
  status: CsvImportStatus;
  /**
   * Present only on partial success: the import was created but the association
   * types for selected custom objects could not be materialized, so those
   * relationships will be missing until the import is retried. The job still
   * succeeds — the records import regardless.
   */
  warning?: string;
}

export type LightfieldMappingAction = MappingAction;

export type LightfieldMappingProposal = MappingProposal;

export interface MappingProposalsResult {
  proposals: Record<string, LightfieldMappingProposal[]>;
  /**
   * Per-entity-type flag: true when the backend capped this entity type's
   * proposals at MAX_PROPOSALS_PER_ENTITY_TYPE (500) and dropped the rest.
   * A truncated entity type's review list is incomplete even though the
   * request succeeded. The dropped tail is the least-populated end of the list.
   */
  truncated: Record<string, boolean>;
  /**
   * Records scanned per entity type — the denominator for `populated_count`
   * ("3 of 285 contacts have this set").
   */
  total_records: Record<string, number>;
}

/** Shape of one entry in a create-import 400's `invalid_mappings` detail. */
export interface InvalidMappingDetail {
  entity_type_name: string;
  raw_key: string;
  target_property_name: string;
}

export interface LightfieldConfirmedMapping {
  raw_key: string;
  entity_type_name: string;
  action: LightfieldMappingAction;
  target_property_name: string;
  field_type_name: string;
  is_multi: boolean;
}

export interface CreateLightfieldImportParams {
  apiKey: string;
  selectedObjectTypes: string[];
  name?: string;
  confirmedMappings?: LightfieldConfirmedMapping[];
}

export async function validateLightfieldKey(
  orgId: number,
  apiKey: string
): Promise<ValidateKeyResponse> {
  return apiPost<ValidateKeyResponse>(
    `/api/orgs/${orgId}/lightfield-import/validate-key/`,
    { api_key: apiKey }
  );
}

export async function discoverLightfieldTypes(
  orgId: number,
  apiKey: string
): Promise<DiscoverTypesResponse> {
  return apiPost<DiscoverTypesResponse>(
    `/api/orgs/${orgId}/lightfield-import/discover-object-types/`,
    { api_key: apiKey }
  );
}

export async function startLightfieldMappingProposals(
  orgId: number,
  params: { apiKey: string; selectedObjectTypes: string[] }
): Promise<StartJobResponse> {
  return apiPost<StartJobResponse>(
    `/api/orgs/${orgId}/lightfield-import/mapping-proposals/`,
    {
      api_key: params.apiKey,
      selected_object_types: params.selectedObjectTypes,
    }
  );
}

export async function startLightfieldImport(
  orgId: number,
  params: CreateLightfieldImportParams
): Promise<StartJobResponse> {
  return apiPost<StartJobResponse>(
    `/api/orgs/${orgId}/lightfield-import/imports/`,
    {
      api_key: params.apiKey,
      selected_object_types: params.selectedObjectTypes,
      ...(params.name ? { name: params.name } : {}),
      ...(params.confirmedMappings
        ? { confirmed_mappings: params.confirmedMappings }
        : {}),
    }
  );
}

export async function getLightfieldJob(
  orgId: number,
  jobId: number,
  signal?: AbortSignal
): Promise<LightfieldJob> {
  return apiGet<LightfieldJob>(
    `/api/orgs/${orgId}/lightfield-import/jobs/${jobId}/`,
    undefined,
    undefined,
    signal
  );
}

export { LightfieldJobFailedError, LightfieldJobTimeoutError };

export async function waitForLightfieldJob(
  orgId: number,
  jobId: number,
  signal?: AbortSignal,
  onStatus?: (status: LightfieldJobStatus) => void
): Promise<LightfieldJob> {
  return waitForVendorJob<LightfieldJob>(
    s => getLightfieldJob(orgId, jobId, s),
    { signal, onStatus, retryAllErrors: true }
  );
}

const JOB_ERROR_COPY: Record<LightfieldJobErrorCode, string> = {
  lightfield_unreachable:
    "We couldn't reach Lightfield. Check your connection and try again.",
  invalid_key: 'That API key is invalid or expired.',
  no_importable_types:
    "Custom objects aren't available on this plan, and no other object types were selected.",
  invalid_mappings:
    'Some mappings are no longer valid. Review them and try again.',
  internal:
    'Something went wrong. Try again, and get in touch if it keeps happening.',
};

export function describeLightfieldError(err: unknown): string {
  if (err instanceof DOMException && err.name === 'AbortError') {
    return 'Import cancelled.';
  }
  // A dispatched job reports its failure in the poll response, not as an HTTP
  // status, so this case comes first — an ApiError here would only ever be a
  // failure of the poll request itself.
  if (err instanceof LightfieldJobFailedError) {
    return (
      JOB_ERROR_COPY[err.code as LightfieldJobErrorCode] ??
      JOB_ERROR_COPY.internal
    );
  }
  if (err instanceof LightfieldJobTimeoutError) {
    return "This is taking longer than expected. It's still running – check the imports list in a few minutes.";
  }
  if (err instanceof ApiError) {
    switch (err.statusCode) {
      case 502:
        return "We couldn't reach Lightfield. Check your connection and try again.";
      case 400:
        // A 400 can be a rejected key OR a bad request body (e.g. invalid
        // selected object types), so stay neutral instead of asserting the key
        // was the problem and pointing the user at the wrong fix.
        return "We couldn't complete that request. Double-check your API key and selections, then try again.";
      case 404:
        return "Lightfield import isn't available for this workspace.";
      default:
        return 'Something went wrong. Try again, and get in touch if it keeps happening.';
    }
  }
  return 'Something went wrong. Try again, and get in touch if it keeps happening.';
}

function extractInvalidMappings(response: unknown): InvalidMappingDetail[] {
  if (
    !response ||
    typeof response !== 'object' ||
    !Array.isArray((response as Record<string, unknown>).invalid_mappings)
  ) {
    return [];
  }
  return (
    (response as Record<string, unknown>).invalid_mappings as unknown[]
  ).filter((entry): entry is InvalidMappingDetail => {
    if (typeof entry !== 'object' || entry === null) {
      return false;
    }
    const candidate = entry as Record<string, unknown>;
    return (
      typeof candidate.entity_type_name === 'string' &&
      typeof candidate.raw_key === 'string' &&
      typeof candidate.target_property_name === 'string'
    );
  });
}

/**
 * Describes a create-import failure. When the backend's 400 carries an
 * `invalid_mappings` detail (a confirmed map_existing target that no longer
 * exists by submit time), names the offending row(s) so the user knows what
 * to fix. Falls back to describeLightfieldError's generic copy otherwise.
 */
export function describeConfirmError(err: unknown): string {
  // The named-rows detail now rides on the failed job's error_detail rather than
  // a 400 body, since the create call returns 202 before the work starts.
  if (
    err instanceof LightfieldJobFailedError &&
    err.code === 'invalid_mappings'
  ) {
    const invalidMappings = extractInvalidMappings(err.detail);
    if (invalidMappings.length > 0) {
      const rows = invalidMappings
        .map(m => `"${m.raw_key}" (${m.entity_type_name})`)
        .join(', ');
      return `These mappings are no longer valid: ${rows}. Review them and try again.`;
    }
  }
  if (err instanceof ApiError && err.statusCode === 400) {
    const invalidMappings = extractInvalidMappings(err.response);
    if (invalidMappings.length > 0) {
      const rows = invalidMappings
        .map(m => `"${m.raw_key}" (${m.entity_type_name})`)
        .join(', ');
      return `These mappings are no longer valid: ${rows}. Review them and try again.`;
    }
  }
  return describeLightfieldError(err);
}
